
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux'

import { useAuth } from './UseAuth'
import { useProject } from './UseProject'
import { useTeams } from './UseTeam';

import History from '../Helpers/History'

import { setProgrammationAction } from '../Redux/Actions/ProgrammationActions'
import { getItemAndItsParentFromTaskList } from '../Helpers/ProgrammationHelper';
import { createGroup, createTask } from '../Services/ProgrammationManagerService';
import { createGroupAction, createTaskAction } from '../Redux/Actions/ProgrammationManagerActions';
import { loadColorCodes, loadDocuments, loadRelations, loadTasklist, loadTasks, loadUsers, loadVersions } from '@/Services/ProgrammationService';

interface Options {
  item_id: string
  schema?: 'macro' | 'lookahead' | 'weekly'
  mode?: 'create' | 'edit'
  noRedirect?: boolean
}

interface Error {
  field: string
  error: string
}
interface ContextValue {
  schema: 'macro' | 'lookahead' | 'weekly'
  mode: 'create' | 'edit'
  teams: any[]
  itemOpened: any | null
  programmationLoaded: boolean
  editingItem: boolean
  errors: Error[]
  loadProgrammation: () => void
  createNewTask: (parent?: any) => void
  createNewGroup: () => void
  openItem: (input: string | Options) => void
  closeItem: () => void
  updateItemField: (fieldName: string, value: string) => void
  patchItemOpened: (patch: any) => void
  setEditingItem: (editingItem: boolean) => void
  setError: (error: Error) => void
  unSetError: (field: string) => void
  clearErrors: () => void
}

const ProgrammationContext = createContext<ContextValue | undefined>(undefined)

export const ProgrammationProvider = ({ children }: { children: ReactNode }) => {

  const dispatch = useDispatch()

  const { ability } = useAuth()
  const { project_id, project, projectLoaded } = useProject()

  const { loadTeams } = useTeams()

  const programmationLoaded = useSelector((state: any) => state.ProgrammationReducer.loadStatus === 'loaded')
  // const { documents } = useSelector((state: any) => state.ProgrammationReducer)
  const macroResult = useSelector((state: any) => state.MacroReducer.result)
  const lookaheadResult = useSelector((state: any) => state.LookaheadReducer.result)
  const weeklyResult = useSelector((state: any) => state.WeeklyReducer.result)

  const [ schema, setSchema ] = useState<'macro' | 'lookahead' | 'weekly'>('macro')
  const [ mode, setMode ] = useState<'create' | 'edit'>('edit')
  const [ itemOpened, setItemOpened ] = useState<any | null>(null)
  const [ editingItem, setEditingItem ] = useState(false)
  const [ errors, setErrors ] = useState<any[]>([])

  const [ list, setList ] = useState<any[]>([])
  const [ tasks, setTasks ] = useState<any[]>([])
  const [ users, setUsers ] = useState<any[]>([])
  const [ colorCodes, setColorCodes ] = useState<any[]>([])
  const [ relations, setRelations ] = useState<any[]>([])
  const [ documents, setDocuments ] = useState<any[]>([])
  const [ versions, setVersions ] = useState<any[]>([])

  const [ listLoaded, setListLoaded ] = useState(false)
  const [ tasksLoaded, setTasksLoaded ] = useState(false)
  const [ usersLoaded, setUsersLoaded ] = useState(false)
  const [ colorCodesLoaded, setColorCodesLoaded ] = useState(false)
  const [ relationsLoaded, setRelationsLoaded ] = useState(false)
  const [ documentsLoaded, setDocumentsLoaded ] = useState(false)
  const [ versionsLoaded, setVersionsLoaded ] = useState(false)

  const loadProjectTaskList = async () => {

    const { data: { taskList }, error: err_taskList} = await loadTasklist(project_id)

    if (!err_taskList) {

      setList(taskList.list)
      setListLoaded(true)
    }
  }

  const loadTasksRelations = async (tasks_ids: string[]) => {

    const { data: { relations }, error: err_relations } = await loadRelations(project_id, tasks_ids)

    if (!err_relations) {

      setRelations(relations)
      setRelationsLoaded(true)
    }
  }

  const loadTasksVersions = async (tasks_ids: string[]) => {

    const { data: { versions }, error: err_versions } = await loadVersions(project_id, tasks_ids)

    if (!err_versions) {

      setVersions(versions)
      setVersionsLoaded(true)
    }
  }

  const loadProjectTasks = async () => {

    const { data: { tasks }, error : err_tasks } = await loadTasks(project_id)

    if (!err_tasks) {

    const tasks_ids = tasks.map((task: any) => task._id)

      setTasks(tasks)
      setTasksLoaded(true)

      loadTasksRelations(tasks_ids)
      loadTasksVersions(tasks_ids)
    }
  }

  const loadProjectColorCodes = async () => {

    const { data: { colorCodes }, error: err_colorcodes } = await loadColorCodes(project_id)

    if (!err_colorcodes) {

      setColorCodes(colorCodes)
      setColorCodesLoaded(true)
    }
  }

  const loadProjectUsers = async () => {

    const { data: { users }, error: err_users } = await loadUsers(project_id)

    if (!err_users) {

      setUsers(users)
      setUsersLoaded(true)
    }
  }

  const loadProjectDocuments = async () => {

    const { data: { documents }, error: err_documents } = await loadDocuments(project_id)

    if (!err_documents) {

      setDocuments(documents)
      setDocumentsLoaded(true)
    }
  }

  const loadProgrammation = async () => {

    dispatch({
      type: 'PROGRAMMATION_LOAD_START'
    })

    loadProjectTaskList()
    loadProjectTasks()
    loadProjectColorCodes()
    loadProjectUsers()
    loadProjectDocuments()
  }

  const createNewTask = async (parent?: any) => {

    const [ error, data ] = await createTask(project_id, parent)

    if (error) return console.log(error)

    setMode('create')

    await dispatch(createTaskAction(data.task, data.taskList, data.parentTask))

    const task = data.task

    if (parent) {

      if (parent.isGroup) {

        task.parentType = 'group'
        task.groupId = parent._id
        task.parentPosition = parent.position
      } else {
        task.parentId = parent._id
      }
      task.parentName = parent.name
    }
    setItemOpened(task)

    // setItemOpened(data.task)
    // openItem(data.task._id)
  }

  const createNewGroup = async () => {

    const [ error, data ] = await createGroup(project_id)

    if (error) return console.log(error)

    setMode('create')
    setItemOpened({
      ...data.group,
      type: 'group',
    })

    dispatch(createGroupAction(data.taskList))
  }

  const openItem = (input: string | Options) => {

    let item_id: string
    let schema: 'macro' | 'lookahead' | 'weekly' = 'macro'
    let mode: 'create' | 'edit' = 'edit'
    let noRedirect = false

    if (typeof input === 'string') {
      item_id = input
    } else {
      item_id = input.item_id
      if (input.schema) schema = input.schema
      if (input.mode) mode = input.mode
      if (input.noRedirect) noRedirect = input.noRedirect
    }

    setSchema(schema)
    setMode(mode)

    let result = macroResult

    if (schema === 'lookahead') {

      result = lookaheadResult
    } else if (schema === 'weekly') {

      result = weeklyResult
    }

    const { item, parent } = getItemAndItsParentFromTaskList(result, item_id)

    if (parent) {

      item.parentPosition = parent?.position

      if (parent?.items) {

        item.parentType = 'group'
      } else {
        item.parentType = parent.type
      }
    }

    if (item.items) {

      item.type = 'group'

      item.parentId = parent?._id
      item.parentName = parent?.name

      setItemOpened(item)

      if (!noRedirect) History.push(`/project/${project._id}/programmation-${schema}?group_id=${item._id}`)
    } else {

      const selectedItem = { ...item }

      selectedItem.documents = documents.filter((document: any) => document.task === item._id)

      if (parent) {

        if (parent?.items) {

          selectedItem.groupId = parent?._id
          selectedItem.groupName = parent?.name
        } else {

          selectedItem.parentId = parent?._id
          selectedItem.parentName = parent?.name
        }
      }

      setItemOpened(selectedItem)

      if (!noRedirect) History.push(`/project/${project._id}/programmation-${schema}?task_id=${item._id}`)
    }
  }

  const updateItemField = async (fieldName: string, value: string) => {

    const item = {...itemOpened}

    item[fieldName] = value

    setItemOpened(item)
  }

  const patchItemOpened = (patch: any) => {

    const item = {
      ...itemOpened,
      ...patch
    }

    setItemOpened(item)
  }

  const closeItem = () => {

    setItemOpened(null)
  }

  const setError = (error: Error) => {

    setErrors([ ...errors, error ])
  }

  const unSetError = (field: string) => {

    setErrors(errors.filter((error: any) => error.field !== field))
  }

  const clearErrors = () => {

    setErrors([])
  }

  useEffect(() => {

    // const loadTeams = async () => {

    //   // await dispatch(TeamsActions.getResultAction(project_id))
    // }

    if (projectLoaded) {

      if (project.ability) ability.update(project.ability)

      loadTeams()
      loadProgrammation()
    } else {

      setList([])
      setTasks([])
      setUsers([])
      setColorCodes([])
      setRelations([])
      setDocuments([])
      setVersions([])

      setListLoaded(false)
      setTasksLoaded(false)
      setUsersLoaded(false)
      setColorCodesLoaded(false)
      setRelationsLoaded(false)
      setDocumentsLoaded(false)
      setVersionsLoaded(false)
    }
  }, [projectLoaded])

  useEffect(() => {

    if (listLoaded && tasksLoaded && usersLoaded && colorCodesLoaded && relationsLoaded && documentsLoaded && versionsLoaded) {

      dispatch(setProgrammationAction({
        list,
        tasks,
        users,
        colorCodes,
        relations,
        documents,
        versions
      }))
    }
  }, [
    listLoaded,
    tasksLoaded,
    usersLoaded,
    colorCodesLoaded,
    relationsLoaded,
    documentsLoaded,
    versionsLoaded
  ])

  const value: ContextValue = {
    schema,
    mode,
    teams: [],
    itemOpened,
    programmationLoaded,
    editingItem,
    loadProgrammation,
    createNewTask,
    createNewGroup,
    errors,
    openItem,
    closeItem,
    updateItemField,
    patchItemOpened,
    setEditingItem,
    setError,
    unSetError,
    clearErrors,
  }

  return (
    <ProgrammationContext.Provider value={value}>
      {children}
    </ProgrammationContext.Provider>
  )
}

export const useProgrammation = (): ContextValue => {

  const context = useContext(ProgrammationContext)

  if (context === undefined) {

    throw new Error('useProgrammation must be used within a ProgrammationProvider')
  }

  return context
}
